import type { ReactNode } from "react";
import { ChipRow, type ChipOption } from "./ChipRow.tsx";
import { SearchField } from "./SearchField.tsx";

/**
 * The strip above a table: a search field, filter chips, and the actions on the right.
 *
 * One row at desktop, stacked on a phone. The search field is `SearchField`, compact. Filters
 * reuse `ChipRow`, wrapped rather than scrolling, because above a table every filter should be
 * visible at once.
 */
export function Toolbar({
  search,
  filters,
  actions,
  children,
}: {
  search?: {
    value: string;
    onChange: (value: string) => void;
    /** "Search members". Doubles as the accessible name. */
    placeholder: string;
  };
  filters?: {
    options: ChipOption[];
    selectedId: string;
    onSelect: (id: string) => void;
    /** The hidden legend, "Filter by status". */
    label: string;
  };
  /** Right-side controls: the primary button, an export, a bulk action. */
  actions?: ReactNode;
  /** Anything else that belongs on the strip, placed after the filters. */
  children?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        {search ? (
          <SearchField
            label={search.placeholder}
            value={search.value}
            onChange={search.onChange}
            className="sm:max-w-[320px]"
          />
        ) : null}
        {filters ? (
          <ChipRow
            options={filters.options}
            selectedId={filters.selectedId}
            onSelect={filters.onSelect}
            label={filters.label}
            wrap
          />
        ) : null}
        {children}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
